// FinanceUmpiring — umpire payment ledger + rate schedule.
//
// Two panels:
//   1. Rate schedule — per-season list of umpire pay rates with
//      effective-from dates. Selecting a match date on the payment
//      form auto-fills the amount from the applicable rate.
//   2. Payment ledger — every umpiring payment with rollup totals
//      per umpire and per team charged.
//
// Umpire and match dropdowns pull from the pool + schedule tables so
// entries stay tied to the rest of the League workspace.

import React, { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import {
  Plus, X, Pencil, Trash2, Gavel, DollarSign, Calendar, ChevronDown
} from "lucide-react";
import { Panel } from "../../../components/primitives";
import { useToast } from "../../../components/Toast";
import { api } from "../../../api";
import {
  fmtMoney, fmtDate, todayIso, PAYMENT_MODES,
  UMPIRE_RATE_GROUNDS, groundBucket
} from "./shared";

const EMPTY_PAY = {
  matchDate: "",
  matchId: "",
  ground: "",
  umpireId: "",
  umpireName: "",
  amount: "",
  mode: "Paid",
  chargedToTeamId: "",
  notes: ""
};

const EMPTY_RATE = {
  ground: "Linbrook",
  effectiveFrom: "",
  effectiveTo: "",
  amount: "",
  notes: ""
};

// Given rates (sorted by effectiveFrom desc), a match date, and a
// ground, return the applicable rate. Falls back to a ground-agnostic
// rate (null Ground) if no exact match found. Undefined if none apply.
function rateForDateAndGround(rates, matchDate, ground) {
  if (!matchDate) return null;
  let fallback = null;
  for (const r of rates) {
    if (r.effectiveFrom && r.effectiveFrom > matchDate) continue;
    if (r.effectiveTo && r.effectiveTo < matchDate) continue;
    if (ground && r.ground && r.ground !== ground) continue;
    if (!r.ground) { if (!fallback) fallback = r; continue; }
    return r;
  }
  return fallback;
}

export default function FinanceUmpiring({ seasonId, canWrite }) {
  const toast = useToast();
  const [payments,  setPayments]  = useState([]);
  const [rates,     setRates]     = useState([]);
  const [umpires,   setUmpires]   = useState([]);
  const [matches,   setMatches]   = useState([]);
  const [teams,     setTeams]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [busy,      setBusy]      = useState(false);
  const [showPay,   setShowPay]   = useState(false);
  const [showRate,  setShowRate]  = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [payForm,   setPayForm]   = useState(EMPTY_PAY);
  const [rateForm,  setRateForm]  = useState(EMPTY_RATE);

  async function refresh() {
    if (!seasonId) return;
    setLoading(true);
    try {
      const [p, r, u, m, t] = await Promise.all([
        api.listUmpiringPayments(seasonId),
        api.listUmpireRates(seasonId),
        api.listUmpires(),
        api.listLeagueMatches(),
        api.listRegisteredTeams()
      ]);
      setPayments(p);
      setRates(r);
      setUmpires(u);
      setMatches(m);
      setTeams(t);
    } catch (err) {
      toast.error(err.message || "Couldn't load umpiring data.");
    } finally { setLoading(false); }
  }
  useEffect(() => { refresh(); /* eslint-disable-next-line */ }, [seasonId]);

  useEffect(() => {
    if (!showPay && !showRate) return;
    const onKey = e => {
      if (e.key !== "Escape") return;
      setShowPay(false); setShowRate(false); setEditingId(null);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [showPay, showRate]);

  // Auto-fill amount from the rate schedule when the match date or
  // ground changes. Ground scoping means Linbrook vs Clarion can carry
  // different rates.
  useEffect(() => {
    if (!showPay || editingId != null) return;
    if (payForm.amount) return; // don't clobber a manually-typed value
    const applicable = rateForDateAndGround(rates, payForm.matchDate, payForm.ground);
    if (applicable) {
      setPayForm(f => ({ ...f, amount: String(applicable.amount) }));
    }
  }, [payForm.matchDate, payForm.ground, rates, editingId, showPay, payForm.amount]);

  // Roll-ups
  const totals = useMemo(() => {
    const paid = payments.reduce((s, p) => s + (p.mode === "Paid"  ? Number(p.amount || 0) : 0), 0);
    const owed = payments.reduce((s, p) => s + (p.mode === "Owed"  ? Number(p.amount || 0) : 0), 0);
    return { paid, owed, count: payments.length };
  }, [payments]);

  const perUmpire = useMemo(() => {
    const map = new Map();
    for (const p of payments) {
      const key = p.umpireName || "Unknown";
      const existing = map.get(key) || { name: key, paid: 0, owed: 0, count: 0 };
      if (p.mode === "Paid") existing.paid += Number(p.amount || 0);
      else                    existing.owed += Number(p.amount || 0);
      existing.count += 1;
      map.set(key, existing);
    }
    return [...map.values()].sort((a, b) => (b.paid + b.owed) - (a.paid + a.owed));
  }, [payments]);

  const perTeam = useMemo(() => {
    const map = new Map();
    for (const p of payments) {
      if (!p.chargedToTeamName) continue;
      const existing = map.get(p.chargedToTeamName) || { team: p.chargedToTeamName, total: 0, count: 0 };
      existing.total += Number(p.amount || 0);
      existing.count += 1;
      map.set(p.chargedToTeamName, existing);
    }
    return [...map.values()].sort((a, b) => b.total - a.total);
  }, [payments]);

  function openCreatePay() {
    setEditingId(null);
    setPayForm({ ...EMPTY_PAY, matchDate: todayIso() });
    setShowPay(true);
  }
  function openEditPay(p) {
    setEditingId(p.id);
    const m = matches.find(mm => String(mm.id) === String(p.matchId));
    setPayForm({
      matchDate:       p.matchDate || todayIso(),
      matchId:         p.matchId != null ? String(p.matchId) : "",
      ground:          groundBucket(m?.ground) || "",
      umpireId:        p.umpireId != null ? String(p.umpireId) : "",
      umpireName:      p.umpireName || "",
      amount:          String(p.amount ?? ""),
      mode:            p.mode || "Paid",
      chargedToTeamId: p.chargedToTeamId != null ? String(p.chargedToTeamId) : "",
      notes:           p.notes || ""
    });
    setShowPay(true);
  }
  function closePay() { setShowPay(false); setEditingId(null); }

  async function submitPay(e) {
    e.preventDefault();
    if (!payForm.matchDate) { toast.error("Match date required."); return; }
    if (!payForm.umpireName.trim()) { toast.error("Umpire name required."); return; }
    if (!payForm.amount)    { toast.error("Amount required."); return; }
    setBusy(true);
    try {
      const payload = {
        seasonId,
        matchDate:       payForm.matchDate,
        matchId:         payForm.matchId || null,
        umpireId:        payForm.umpireId || null,
        umpireName:      payForm.umpireName.trim(),
        amount:          Number(payForm.amount),
        mode:            payForm.mode,
        chargedToTeamId: payForm.chargedToTeamId || null,
        notes:           payForm.notes || null
      };
      if (editingId) {
        await api.updateUmpiringPayment(editingId, payload);
        toast.success("Payment updated.");
      } else {
        await api.createUmpiringPayment(payload);
        toast.success("Payment recorded.");
      }
      closePay();
      await refresh();
    } catch (err) {
      toast.error(err.message || "Save failed.");
    } finally { setBusy(false); }
  }

  async function removePay(p) {
    if (!confirm(`Delete this ${fmtMoney(p.amount)} payment to ${p.umpireName}?`)) return;
    try {
      await api.deleteUmpiringPayment(p.id);
      toast.success("Deleted.");
      await refresh();
    } catch (err) { toast.error(err.message || "Delete failed."); }
  }

  async function submitRate(e) {
    e.preventDefault();
    if (!rateForm.ground) { toast.error("Ground is required."); return; }
    if (!rateForm.effectiveFrom) { toast.error("Effective-from date required."); return; }
    if (!rateForm.amount) { toast.error("Amount required."); return; }
    setBusy(true);
    try {
      await api.createUmpireRate({
        seasonId,
        ground:        rateForm.ground,
        effectiveFrom: rateForm.effectiveFrom,
        effectiveTo:   rateForm.effectiveTo || null,
        amount:        Number(rateForm.amount),
        notes:         rateForm.notes || null
      });
      toast.success("Rate added.");
      setShowRate(false);
      setRateForm(EMPTY_RATE);
      await refresh();
    } catch (err) {
      toast.error(err.message || "Save failed.");
    } finally { setBusy(false); }
  }

  async function removeRate(r) {
    if (!confirm(`Delete this ${fmtMoney(r.amount)} rate?`)) return;
    try {
      await api.deleteUmpireRate(r.id);
      toast.success("Deleted.");
      await refresh();
    } catch (err) { toast.error(err.message || "Delete failed."); }
  }

  return (
    <div className="stack" style={{gap:16}}>
      {/* KPI strip */}
      <div className="fin-kpis">
        <div className="fin-kpi fin-kpi--red">
          <span className="fin-kpi-icon"><DollarSign size={18}/></span>
          <span className="fin-kpi-label">Total Paid</span>
          <b className="fin-kpi-value">{fmtMoney(totals.paid)}</b>
        </div>
        <div className="fin-kpi fin-kpi--gold">
          <span className="fin-kpi-icon"><DollarSign size={18}/></span>
          <span className="fin-kpi-label">Still Owed</span>
          <b className="fin-kpi-value">{fmtMoney(totals.owed)}</b>
        </div>
        <div className="fin-kpi fin-kpi--cyan">
          <span className="fin-kpi-icon"><Gavel size={18}/></span>
          <span className="fin-kpi-label">Payments Logged</span>
          <b className="fin-kpi-value">{totals.count}</b>
        </div>
        <div className="fin-kpi fin-kpi--violet">
          <span className="fin-kpi-icon"><Calendar size={18}/></span>
          <span className="fin-kpi-label">Rate Tiers</span>
          <b className="fin-kpi-value">{rates.length}</b>
        </div>
      </div>

      {/* Rate schedule */}
      <Panel title={
        <div style={{display:"flex", alignItems:"center", gap:10, flex:1, flexWrap:"wrap"}}>
          <Calendar size={18}/>
          <span>Rate Schedule</span>
          {canWrite && (
            <button type="button" className="btn"
              onClick={() => { setRateForm(EMPTY_RATE); setShowRate(true); }}
              style={{marginLeft:"auto"}}>
              <Plus size={13}/> Add Rate
            </button>
          )}
        </div>
      }>
        {rates.length === 0 && (
          <p style={{color:"var(--muted)", margin:0}}>
            No rates set. Add one so new payment entries auto-fill the amount.
          </p>
        )}
        {rates.length > 0 && (
          <div className="fin-rateList">
            {rates.map(r => (
              <article
                key={r.id}
                className={`fin-rateCard fin-rateCard--${(r.ground || "any").toLowerCase()}`}
              >
                <div className="fin-rateCard-amount">{fmtMoney(r.amount)}</div>
                <div className="fin-rateCard-body">
                  <div className="fin-rateCard-tags">
                    <span className={`fin-groundPill fin-groundPill--${(r.ground || "any").toLowerCase()}`}>
                      {r.ground || "All Grounds"}
                    </span>
                  </div>
                  <b>{fmtDate(r.effectiveFrom)}</b>
                  <small>
                    {r.effectiveTo ? `through ${fmtDate(r.effectiveTo)}` : "Ongoing"}
                    {r.notes ? ` · ${r.notes}` : ""}
                  </small>
                </div>
                {canWrite && (
                  <button type="button"
                    className="league-annCard-iconBtn league-annCard-iconBtn--danger"
                    onClick={() => removeRate(r)} title="Delete">
                    <Trash2 size={12}/>
                  </button>
                )}
              </article>
            ))}
          </div>
        )}
      </Panel>

      {/* Payment ledger */}
      <Panel title={
        <div style={{display:"flex", alignItems:"center", gap:10, flex:1, flexWrap:"wrap"}}>
          <Gavel size={18}/>
          <span>Payment Ledger</span>
          {canWrite && (
            <button type="button" className="btn" onClick={openCreatePay} style={{marginLeft:"auto"}}>
              <Plus size={13}/> Record Payment
            </button>
          )}
        </div>
      }>
        {loading && <p style={{color:"var(--muted)"}}>Loading…</p>}
        {!loading && payments.length === 0 && (
          <div className="league-annEmpty">
            <Gavel size={28}/>
            <p>No umpiring payments recorded for this season yet.</p>
          </div>
        )}
        {payments.length > 0 && (
          <div className="fin-tableWrap">
            <table className="fin-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Umpire</th>
                  <th>Match</th>
                  <th>Charged To</th>
                  <th style={{textAlign:"right"}}>Amount</th>
                  <th>Status</th>
                  {canWrite && <th className="fin-table-actions">Actions</th>}
                </tr>
              </thead>
              <tbody>
                {payments.map(p => (
                  <tr key={p.id}>
                    <td>{fmtDate(p.matchDate)}</td>
                    <td><b>{p.umpireName}</b></td>
                    <td>{p.matchLabel || "—"}</td>
                    <td>{p.chargedToTeamName || "—"}</td>
                    <td className="fin-table-money">{fmtMoney(p.amount)}</td>
                    <td>
                      <span className={`fin-pill fin-pill--${p.mode === "Paid" ? "green" : "gold"}`}>
                        {p.mode}
                      </span>
                    </td>
                    {canWrite && (
                      <td className="fin-table-actions">
                        <button type="button" className="league-annCard-iconBtn"
                          onClick={() => openEditPay(p)} title="Edit">
                          <Pencil size={12}/>
                        </button>
                        <button type="button" className="league-annCard-iconBtn league-annCard-iconBtn--danger"
                          onClick={() => removePay(p)} title="Delete">
                          <Trash2 size={12}/>
                        </button>
                      </td>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>

      {/* Rollups */}
      {(perUmpire.length > 0 || perTeam.length > 0) && (
        <div className="fin-breakdown">
          {perUmpire.length > 0 && (
            <Panel title={<><Gavel size={16}/> By Umpire</>}>
              <div className="fin-ledger">
                {perUmpire.map(u => (
                  <div key={u.name} className="fin-ledger-row fin-ledger-row--cyan">
                    <div className="fin-ledger-name">
                      <b>{u.name}</b>
                      <small>{u.count} matches</small>
                    </div>
                    <div className="fin-ledger-bar"/>
                    <div className="fin-ledger-amount">
                      {fmtMoney(u.paid + u.owed)}
                      {u.owed > 0 && (
                        <small style={{display:"block", fontSize:10, color:"var(--gold, #f7c948)"}}>
                          {fmtMoney(u.owed)} owed
                        </small>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </Panel>
          )}
          {perTeam.length > 0 && (
            <Panel title={<><DollarSign size={16}/> Charged to Teams</>}>
              <div className="fin-ledger">
                {perTeam.map(t => (
                  <div key={t.team} className="fin-ledger-row fin-ledger-row--violet">
                    <div className="fin-ledger-name">
                      <b>{t.team}</b>
                      <small>{t.count} charges</small>
                    </div>
                    <div className="fin-ledger-bar"/>
                    <div className="fin-ledger-amount">{fmtMoney(t.total)}</div>
                  </div>
                ))}
              </div>
            </Panel>
          )}
        </div>
      )}

      {/* Payment form */}
      {showPay && createPortal((
        <div className="modal" onClick={closePay}>
          <div className="modalInner" onClick={e => e.stopPropagation()}>
            <button type="button" className="close" onClick={closePay} aria-label="Close">
              <X size={20}/>
            </button>
            <Panel title={editingId ? "Edit Umpiring Payment" : "Record Umpiring Payment"}>
              <form className="form" onSubmit={submitPay}>
                <div className="two">
                  <div>
                    <label>Match Date *</label>
                    <input type="date" required
                      value={payForm.matchDate}
                      onChange={e => setPayForm({ ...payForm, matchDate: e.target.value })}/>
                  </div>
                  <div>
                    <label>Match</label>
                    <select value={payForm.matchId}
                      onChange={e => {
                        const id = e.target.value;
                        const m = matches.find(m => String(m.id) === String(id));
                        setPayForm(f => ({
                          ...f,
                          matchId: id,
                          matchDate: m?.date || f.matchDate,
                          // Snap ground to the bucket implied by the
                          // picked match's ground (Linbrook / Clarion).
                          ground: groundBucket(m?.ground) || f.ground
                        }));
                      }}>
                      <option value="">— optional —</option>
                      {matches
                        .filter(m => !payForm.matchDate ||
                          (m.date && m.date <= todayIso()) ||
                          m.date === payForm.matchDate)
                        .slice(0, 200)
                        .map(m => (
                          <option key={m.id} value={m.id}>
                            {m.date} · {m.teamOne} vs {m.teamTwo}
                            {m.ground ? ` · ${m.ground}` : ""}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                <label>Ground</label>
                <div className="fin-groundChoice" role="radiogroup" aria-label="Ground">
                  {UMPIRE_RATE_GROUNDS.map(g => (
                    <button
                      key={g}
                      type="button"
                      role="radio"
                      aria-checked={payForm.ground === g}
                      className={`fin-groundChoice-btn fin-groundChoice-btn--${g.toLowerCase()}${payForm.ground === g ? " fin-groundChoice-btn--active" : ""}`}
                      onClick={() => setPayForm({ ...payForm, ground: g, amount: "" })}
                    >{g}</button>
                  ))}
                </div>

                <div className="two">
                  <div>
                    <label>Umpire (from pool)</label>
                    <select value={payForm.umpireId}
                      onChange={e => {
                        const id = e.target.value;
                        const u = umpires.find(u => String(u.id) === String(id));
                        setPayForm(f => ({
                          ...f,
                          umpireId: id,
                          umpireName: u ? `${u.firstName || ""} ${u.lastName || ""}`.trim() : f.umpireName
                        }));
                      }}>
                      <option value="">— pick from umpire pool —</option>
                      {umpires
                        .sort((a, b) => (a.firstName || "").localeCompare(b.firstName || ""))
                        .map(u => (
                          <option key={u.id} value={u.id}>
                            {u.firstName} {u.lastName}{u.teamName ? ` · ${u.teamName}` : ""}
                          </option>
                        ))}
                    </select>
                  </div>
                  <div>
                    <label>Umpire Name *</label>
                    <input required
                      value={payForm.umpireName}
                      onChange={e => setPayForm({ ...payForm, umpireName: e.target.value })}
                      placeholder="Kaustav Mukherjee"/>
                  </div>
                </div>

                <div className="two">
                  <div>
                    <label>Amount *</label>
                    <input type="number" step="0.01" required
                      value={payForm.amount}
                      onChange={e => setPayForm({ ...payForm, amount: e.target.value })}
                      placeholder={rateForDateAndGround(rates, payForm.matchDate, payForm.ground)
                        ? String(rateForDateAndGround(rates, payForm.matchDate, payForm.ground).amount) : "90.00"}/>
                    {rateForDateAndGround(rates, payForm.matchDate, payForm.ground) && !payForm.amount && (
                      <small style={{color:"var(--muted)", fontSize:11}}>
                        Rate for {payForm.ground || "any ground"} on {fmtDate(payForm.matchDate)}: {fmtMoney(rateForDateAndGround(rates, payForm.matchDate, payForm.ground).amount)}
                      </small>
                    )}
                  </div>
                  <div>
                    <label>Status</label>
                    <select value={payForm.mode}
                      onChange={e => setPayForm({ ...payForm, mode: e.target.value })}>
                      <option value="Paid">Paid</option>
                      <option value="Owed">Owed</option>
                    </select>
                  </div>
                </div>

                <label>Charged to Team (optional)</label>
                <select value={payForm.chargedToTeamId}
                  onChange={e => setPayForm({ ...payForm, chargedToTeamId: e.target.value })}>
                  <option value="">— none / league wide —</option>
                  {teams
                    .filter(t => t.status !== "Rejected")
                    .sort((a, b) => (a.teamName || "").localeCompare(b.teamName || ""))
                    .map(t => (
                      <option key={t.id} value={t.id}>{t.teamName}</option>
                    ))}
                </select>

                <label>Notes</label>
                <input value={payForm.notes}
                  onChange={e => setPayForm({ ...payForm, notes: e.target.value })}
                  placeholder="e.g. EUCC vs PittKings"/>

                <div style={{display:"flex", gap:10, justifyContent:"flex-end", marginTop:6}}>
                  <button type="button" className="dark" onClick={closePay} disabled={busy}>Cancel</button>
                  <button type="submit" className="btn" disabled={busy}>
                    {busy ? "Saving…" : editingId ? "Save Changes" : "Record Payment"}
                  </button>
                </div>
              </form>
            </Panel>
          </div>
        </div>
      ), document.body)}

      {/* Rate form */}
      {showRate && createPortal((
        <div className="modal" onClick={() => setShowRate(false)}>
          <div className="modalInner" onClick={e => e.stopPropagation()}>
            <button type="button" className="close" onClick={() => setShowRate(false)} aria-label="Close">
              <X size={20}/>
            </button>
            <Panel title="Add Umpire Rate">
              <form className="form" onSubmit={submitRate}>
                <label>Ground *</label>
                <div className="fin-groundChoice" role="radiogroup" aria-label="Ground">
                  {UMPIRE_RATE_GROUNDS.map(g => (
                    <button
                      key={g}
                      type="button"
                      role="radio"
                      aria-checked={rateForm.ground === g}
                      className={`fin-groundChoice-btn fin-groundChoice-btn--${g.toLowerCase()}${rateForm.ground === g ? " fin-groundChoice-btn--active" : ""}`}
                      onClick={() => setRateForm({ ...rateForm, ground: g })}
                    >{g}</button>
                  ))}
                </div>
                <small style={{color:"var(--muted)", fontSize:11, marginTop:-4}}>
                  Rates are ground-scoped. Clarion covers CC-1 and CC-2 as sub-grounds.
                </small>

                <div className="two">
                  <div>
                    <label>Effective From *</label>
                    <input type="date" required
                      value={rateForm.effectiveFrom}
                      onChange={e => setRateForm({ ...rateForm, effectiveFrom: e.target.value })}/>
                  </div>
                  <div>
                    <label>Effective To</label>
                    <input type="date"
                      value={rateForm.effectiveTo}
                      onChange={e => setRateForm({ ...rateForm, effectiveTo: e.target.value })}/>
                  </div>
                </div>

                <label>Amount per match *</label>
                <input type="number" step="0.01" required
                  value={rateForm.amount}
                  onChange={e => setRateForm({ ...rateForm, amount: e.target.value })}
                  placeholder="90.00"/>

                <label>Notes</label>
                <input value={rateForm.notes}
                  onChange={e => setRateForm({ ...rateForm, notes: e.target.value })}
                  placeholder="Base rate / playoffs bump / etc."/>

                <div style={{display:"flex", gap:10, justifyContent:"flex-end", marginTop:6}}>
                  <button type="button" className="dark" onClick={() => setShowRate(false)} disabled={busy}>Cancel</button>
                  <button type="submit" className="btn" disabled={busy}>
                    {busy ? "Saving…" : "Add Rate"}
                  </button>
                </div>
              </form>
            </Panel>
          </div>
        </div>
      ), document.body)}
    </div>
  );
}
